import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { NbDialogRef, NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { IBucket } from '../../../services/bucket.service';
import { CreatorLibraryService } from '../../../services/creator-library.service';
import { CreatorMoodleDialogComponent } from '../creator-moodle-dialog/creator-moodle-dialog.component';
import { CreatorAcCourseDialogComponent } from '../creator-ac-course-dialog/creator-ac-course-dialog.component';
import {
  ExerciseCreateConfig,
  ExerciseCreateDialogComponent,
  exerciseWorkspaceParams,
} from '../exercise-create-dialog/exercise-create-dialog.component';

export type CreatorStartKind = 'course' | 'exercise';

/**
 * The Creator start dialog (Creator › start, a tile): one dialog for both
 * ways, after the Creator design draft 03 (Marcel, 28.10.2026).
 *
 * Course: first what is made — interactive E-Learning (course editor, SCORM
 * export) or a Moodle course — then how. E-Learning "selbst gestalten" opens
 * the existing „Kurs von Hand bauen" dialog; E-Learning with AI is not built
 * yet („Bald"). Moodle asks for name and description (`creator-moodle-dialog`) and
 * then starts the canvas quick start (`mode=course` with the values).
 *
 * Exercise: on a topic or from knowledge bases. Both open the exercise dialog
 * of Dozent › Übungen (`topic` / `kb`), which asks title, description, goals
 * and question types first — the AI refuses an exercise without a
 * description, and without it the Creator opened an empty, failing run (#223).
 */
@Component({
  selector: 'app-creator-start-dialog',
  templateUrl: './creator-start-dialog.component.html',
  styleUrl: './creator-start-dialog.component.scss',
})
export class CreatorStartDialogComponent {
  @Input() kind: CreatorStartKind = 'course';

  /** The course editor runs only where its host exists (environment). */
  readonly manualAvailable = !!environment.creatorCourseEditor;

  format: 'elearning' | 'moodle' | null = null;
  /** Knowledge bases are loading for the "Wissensbasis verwenden" way. */
  loadingBuckets = false;

  constructor(
    private ref: NbDialogRef<CreatorStartDialogComponent>,
    private dialogs: NbDialogService,
    private library: CreatorLibraryService,
    private router: Router,
    private toastr: NbToastrService,
    private t: TranslateService,
  ) {}

  /** A note's first line (the lead, „Hierfür kannst du …“) — set off from the list below it. */
  noteLead(key: string): string {
    return String(this.t.instant(key)).split('\n')[0];
  }

  /** The note's other lines (the list), one per line. */
  noteRest(key: string): string {
    return String(this.t.instant(key)).split('\n').slice(1).join('\n');
  }

  close(): void {
    this.ref.close();
  }

  pickFormat(format: 'elearning' | 'moodle' | null): void {
    this.format = format;
  }

  byHand(): void {
    if (!this.manualAvailable) return;
    this.ref.close();
    this.dialogs.open(CreatorAcCourseDialogComponent, { closeOnBackdropClick: false });
  }

  moodleWithAi(): void {
    // name and description first, like „Neue Übung"; the dialog hands them on
    this.ref.close();
    this.dialogs.open(CreatorMoodleDialogComponent, { closeOnBackdropClick: false });
  }

  exerciseOnTopic(): void {
    this.openExerciseDialog('topic', []);
  }

  exerciseFromKnowledge(): void {
    if (this.loadingBuckets) return;
    this.loadingBuckets = true;
    this.library
      .buckets()
      .pipe(finalize(() => (this.loadingBuckets = false)))
      .subscribe({
        next: (buckets) => this.openExerciseDialog('kb', buckets),
        error: () => this.toastr.danger(this.t.instant('pages.creator.library.loadError'), ''),
      });
  }

  private openExerciseDialog(mode: 'topic' | 'kb', buckets: IBucket[]): void {
    this.ref.close();
    this.dialogs.open(ExerciseCreateDialogComponent, {
      closeOnBackdropClick: false,
      context: {
        mode,
        buckets,
        onConfirm: (config: ExerciseCreateConfig) =>
          this.router.navigate(['/creator/workspace'], { queryParams: exerciseWorkspaceParams(config) }),
      },
    });
  }
}
